import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { motion } from "framer-motion";
import { Input } from "../ui/input";
import { fetchUserProfile, updateUserPassword } from "@/hooks/useUser";
import { useToast } from "../../hooks/use-toast";

export default function SettingPage() {
  const { toast } = useToast();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Fetch profile on mount
  useEffect(() => {
    const getProfile = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const data = await fetchUserProfile(userId);
        setUser(data.user);
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };
    getProfile();
  }, []);

  const handlePasswordUpdate = async () => {
    try {
      const userId = localStorage.getItem("userId");

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast({
          title: "Error",
          description: "New passwords do not match",
          variant: "destructive",
        });
        return;
      }

      await updateUserPassword(userId, {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      toast({
        title: "Success",
        description: "Password updated successfully",
      });

      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update password",
        variant: "destructive",
      });
    }
  };

  if (loading) return <p className="p-6">Loading...</p>;

  return (
    <div className="min-h-screen w-full bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <h2 className="text-3xl font-bold">Settings</h2>

        {/* Profile Card */}
        <Card>
          <CardContent className="p-6">
            <CardTitle className="text-md mb-4">Profile Settings</CardTitle>
            <Input value={user?.name || ""} disabled className="mb-4" />
            <Input value={user?.email || ""} disabled className="mb-4" />
            <Badge variant="secondary">{user?.role || "User"}</Badge>
          </CardContent>
        </Card>

        {/* Update Password */}
        <Card>
          <CardContent className="p-6">
            <CardTitle className="text-md mb-4">Update Password</CardTitle>
            <Input
              placeholder="Current Password"
              type="password"
              className="mb-4"
              value={passwordData.oldPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({
                  ...prev,
                  oldPassword: e.target.value,
                }))
              }
            />
            <Input
              placeholder="New Password"
              type="password"
              className="mb-4"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({
                  ...prev,
                  newPassword: e.target.value,
                }))
              }
            />
            <Input
              placeholder="Confirm New Password"
              type="password"
              className="mb-4"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({
                  ...prev,
                  confirmPassword: e.target.value,
                }))
              }
            />
            <Button
              onClick={handlePasswordUpdate}
              className="bg-purple-600 hover:bg-purple-700 w-full"
            >
              Update Password
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
